import { useState } from "react";
import type { QuadraPiloto } from "@/types/piloto";

interface PilotBlocksTableProps {
  quadras: QuadraPiloto[];
}

function getStatusLabel(status: string): string {
  switch (status) {
    case "nao_iniciado": return "Não iniciado";
    case "em_campo": return "Em campo";
    case "processado": return "Processado";
    case "validado": return "Validado";
    default: return status;
  }
}

function getStatusColor(status: string): string {
  switch (status) {
    case "nao_iniciado": return "bg-background-100 text-foreground-500 border-background-200";
    case "em_campo": return "bg-accent-100 text-accent-700 border-accent-200";
    case "processado": return "bg-caution-100 text-caution-700 border-caution-200";
    case "validado": return "bg-primary-100 text-primary-700 border-primary-200";
    default: return "bg-background-100 text-foreground-500";
  }
}

function getPrioridadeColor(p: string): string {
  switch (p) {
    case "baixa": return "text-primary-600";
    case "media": return "text-caution-600";
    case "alta": return "text-warning-600";
    case "critica": return "text-danger-600";
    default: return "text-foreground-500";
  }
}

function getIaqBadge(classe: string | undefined): { label: string; color: string } {
  switch (classe) {
    case "alta": return { label: "Alta", color: "bg-primary-100 text-primary-700" };
    case "media": return { label: "Média", color: "bg-caution-100 text-caution-700" };
    case "baixa": return { label: "Baixa", color: "bg-warning-100 text-warning-700" };
    case "critica": return { label: "Crítica", color: "bg-danger-100 text-danger-700" };
    default: return { label: "—", color: "bg-background-100 text-foreground-400" };
  }
}

export default function PilotBlocksTable({ quadras }: PilotBlocksTableProps) {
  const [filterStatus, setFilterStatus] = useState<string>("");
  const [filterPrioridade, setFilterPrioridade] = useState<string>("");
  const [searchQuadra, setSearchQuadra] = useState("");

  const filtered = quadras.filter((q) => {
    if (filterStatus && q.statusLevantamento !== filterStatus) return false;
    if (filterPrioridade && q.prioridadePlantio !== filterPrioridade) return false;
    if (searchQuadra && !q.codigo.toLowerCase().includes(searchQuadra.toLowerCase()) && !q.nome.toLowerCase().includes(searchQuadra.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="bg-white rounded-card border border-background-200/70 overflow-hidden flex flex-col">
      {/* Header */}
      <div className="px-4 py-3 border-b border-background-200/70 flex flex-col sm:flex-row sm:items-center gap-2">
        <div className="flex items-center gap-2">
          <i className="ri-grid-line text-primary-500 text-sm"></i>
          <h3 className="text-sm font-semibold text-foreground-800 font-heading">Quadras da Área Piloto</h3>
          <span className="text-[10px] font-medium text-foreground-500 bg-background-100 px-2 py-0.5 rounded-full">
            {filtered.length} de {quadras.length}
          </span>
        </div>
        <div className="flex gap-2 sm:ml-auto">
          <input
            type="text"
            placeholder="Buscar quadra..."
            value={searchQuadra}
            onChange={(e) => setSearchQuadra(e.target.value)}
            className="text-xs border border-background-200 rounded-md px-2.5 py-1.5 bg-white text-foreground-700 focus:outline-none focus:border-accent-400 w-36"
          />
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="text-xs border border-background-200 rounded-md px-2 py-1.5 bg-white text-foreground-700 focus:outline-none focus:border-accent-400"
          >
            <option value="">Todos status</option>
            <option value="nao_iniciado">Não iniciado</option>
            <option value="em_campo">Em campo</option>
            <option value="processado">Processado</option>
            <option value="validado">Validado</option>
          </select>
          <select
            value={filterPrioridade}
            onChange={(e) => setFilterPrioridade(e.target.value)}
            className="text-xs border border-background-200 rounded-md px-2 py-1.5 bg-white text-foreground-700 focus:outline-none focus:border-accent-400"
          >
            <option value="">Todas prioridades</option>
            <option value="baixa">Baixa</option>
            <option value="media">Média</option>
            <option value="alta">Alta</option>
            <option value="critica">Crítica</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-left">
          <thead>
            <tr className="bg-background-50 border-b border-background-200/70">
              <th className="px-3 py-2 text-[10px] font-semibold text-foreground-600 uppercase tracking-wider">Código</th>
              <th className="px-3 py-2 text-[10px] font-semibold text-foreground-600 uppercase tracking-wider">Status</th>
              <th className="px-3 py-2 text-[10px] font-semibold text-foreground-600 uppercase tracking-wider text-right">Árvores</th>
              <th className="px-3 py-2 text-[10px] font-semibold text-foreground-600 uppercase tracking-wider text-right">Risco</th>
              <th className="px-3 py-2 text-[10px] font-semibold text-foreground-600 uppercase tracking-wider text-right">Conflitos</th>
              <th className="px-3 py-2 text-[10px] font-semibold text-foreground-600 uppercase tracking-wider text-right">Cobertura</th>
              <th className="px-3 py-2 text-[10px] font-semibold text-foreground-600 uppercase tracking-wider">Prioridade</th>
              <th className="px-3 py-2 text-[10px] font-semibold text-foreground-600 uppercase tracking-wider">IAQ</th>
              <th className="px-3 py-2 text-[10px] font-semibold text-foreground-600 uppercase tracking-wider">ICV</th>
              <th className="px-3 py-2 text-[10px] font-semibold text-foreground-600 uppercase tracking-wider">IPP</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-background-200/50">
            {filtered.map((q) => {
              const iaq = getIaqBadge(q.iaq);
              return (
                <tr key={q.id} className="hover:bg-background-50/50 transition-colors">
                  <td className="px-3 py-2">
                    <span className="text-xs font-mono font-medium text-foreground-800">{q.codigo}</span>
                  </td>
                  <td className="px-3 py-2">
                    <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full border whitespace-nowrap ${getStatusColor(q.statusLevantamento)}`}>
                      {getStatusLabel(q.statusLevantamento)}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <span className="text-xs font-medium text-foreground-700">{q.totalArvores}</span>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <span className={`text-xs font-medium ${q.arvoresComRisco > 0 ? "text-danger-600" : "text-foreground-400"}`}>
                      {q.arvoresComRisco}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <span className={`text-xs font-medium ${q.arvoresComConflito > 0 ? "text-warning-600" : "text-foreground-400"}`}>
                      {q.arvoresComConflito}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <span className={`text-xs font-medium ${q.coberturaCopaPercentual < 10 ? "text-danger-600" : "text-foreground-700"}`}>
                      {q.coberturaCopaPercentual}%
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <span className={`text-[10px] font-medium ${getPrioridadeColor(q.prioridadePlantio)}`}>
                      {q.prioridadePlantio.charAt(0).toUpperCase() + q.prioridadePlantio.slice(1)}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full ${iaq.color}`}>
                      {iaq.label}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full ${getIaqBadge(q.icv).color}`}>
                      {q.icv ? (q.icv.charAt(0).toUpperCase() + q.icv.slice(1).replace("_", " ")) : "—"}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full ${getIaqBadge(q.ipp).color}`}>
                      {q.ipp ? (q.ipp.charAt(0).toUpperCase() + q.ipp.slice(1)) : "—"}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {filtered.length === 0 && (
        <div className="p-6 text-center">
          <div className="w-10 h-10 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-2">
            <i className="ri-search-line text-foreground-400 text-lg"></i>
          </div>
          <p className="text-xs text-foreground-500">Nenhuma quadra encontrada com os filtros atuais</p>
        </div>
      )}
    </div>
  );
}